import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { isNative, splashScreen, statusBar, appLifecycle } from "./utils/capacitor";
import { Toaster } from "./components/ui/sonner";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { OfflineBanner } from "./components/OfflineBanner";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { IOSInstallGuide } from "./components/IOSInstallGuide";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MembershipPage from "./pages/MembershipPage";
import MembershipSuccessPage from "./pages/MembershipSuccessPage";
import ReferralDashboardPage from "./pages/ReferralDashboardPage";
import AgentClientsPage from "./pages/AgentClientsPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import NewTripPage from "./pages/NewTripPage";
import ItineraryPage from "./pages/ItineraryPage";
import PackingListPage from "./pages/PackingListPage";
import TripComparePage from "./pages/TripComparePage";
import BudgetTrackerPage from "./pages/BudgetTrackerPage";
import CalculateBudgetPage from "./pages/CalculateBudgetPage";
import ProfilePage from "./pages/ProfilePage";
import ContactPage from "./pages/ContactPage";
import FlightSearchPage from "./pages/FlightSearchPage";
import HotelSearchPage from "./pages/HotelSearchPage";
import TourPackagesPage from "./pages/TourPackagesPage";
import TravelServicesPage from "./pages/TravelServicesPage";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import MyJournalPage from "./pages/MyJournalPage";
import SharedJournalPage from "./pages/SharedJournalPage";
import TemplatesPage from "./pages/TemplatesPage";
import TripTimelinePage from "./pages/TripTimelinePage";
import SharedTimelinePage from "./pages/SharedTimelinePage";
import TripBlogPage from "./pages/TripBlogPage";
import SharedBlogPage from "./pages/SharedBlogPage";
import MyStoriesPage from "./pages/MyStoriesPage";
import MyBlogsPage from "./pages/MyBlogsPage";
import MyBudgetsPage from "./pages/MyBudgetsPage";
import FAQPage from "./pages/FAQPage";
import VisaCheckPage from "./pages/VisaCheckPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AirportTransfersPage from "./pages/AirportTransfersPage";
import CarRentalsPage from "./pages/CarRentalsPage";
import ActivitiesAttractionsPage from "./pages/ActivitiesAttractionsPage";
import TravelInsurancePage from "./pages/TravelInsurancePage";
import FlightCompensationPage from "./pages/FlightCompensationPage";
import EsimPage from "./pages/EsimPage";
import LuggageStoragePage from "./pages/LuggageStoragePage";
import LocalTripsPage from "./pages/LocalTripsPage";
import AssistedServicesPage from "./pages/AssistedServicesPage";
import WebsiteReviewsPage from "./pages/WebsiteReviewsPage";
import Footer from "./components/Footer";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full" />
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full" />
    </div>
  );
  if (isAuthenticated) return <Navigate to={redirectUrl} replace />;
  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: Detect session_id synchronously during render (NOT in useEffect)
  // This processes the Google OAuth callback BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/membership/success" element={<MembershipSuccessPage />} />
      <Route path="/referrals" element={<ProtectedRoute><ReferralDashboardPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><AgentClientsPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/new-trip" element={<NewTripPage />} />
      <Route path="/itinerary/:id" element={<ProtectedRoute><ItineraryPage /></ProtectedRoute>} />
      <Route path="/packing-list" element={<ProtectedRoute><PackingListPage /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><TripComparePage /></ProtectedRoute>} />
      <Route path="/itinerary/:id/budget" element={<ProtectedRoute><BudgetTrackerPage /></ProtectedRoute>} />
      <Route path="/calculate-budget" element={<ProtectedRoute><CalculateBudgetPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><MyJournalPage /></ProtectedRoute>} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/flights" element={<FlightSearchPage />} />
      <Route path="/hotels" element={<HotelSearchPage />} />
      <Route path="/tour-packages" element={<TourPackagesPage />} />
      <Route path="/travel-services" element={<TravelServicesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/shared/journal/:shareId" element={<SharedJournalPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms-of-use" element={<TermsOfUsePage />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/itinerary/:id/timeline" element={<ProtectedRoute><TripTimelinePage /></ProtectedRoute>} />
      <Route path="/shared/timeline/:shareId" element={<SharedTimelinePage />} />
      <Route path="/itinerary/:id/blog" element={<ProtectedRoute><TripBlogPage /></ProtectedRoute>} />
      <Route path="/shared/blog/:shareId" element={<SharedBlogPage />} />
      <Route path="/stories" element={<ProtectedRoute><MyStoriesPage /></ProtectedRoute>} />
      <Route path="/blogs" element={<ProtectedRoute><MyBlogsPage /></ProtectedRoute>} />
      <Route path="/budgets" element={<ProtectedRoute><MyBudgetsPage /></ProtectedRoute>} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/visa-check" element={<ProtectedRoute><VisaCheckPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/airport-transfers" element={<AirportTransfersPage />} />
      <Route path="/car-rentals" element={<CarRentalsPage />} />
      <Route path="/activities" element={<ActivitiesAttractionsPage />} />
      <Route path="/travel-insurance" element={<TravelInsurancePage />} />
      <Route path="/flight-compensation" element={<FlightCompensationPage />} />
      <Route path="/esim" element={<EsimPage />} />
      <Route path="/luggage-storage" element={<LuggageStoragePage />} />
      <Route path="/local-trips" element={<LocalTripsPage />} />
      <Route path="/assisted-services" element={<AssistedServicesPage />} />
      <Route path="/reviews" element={<WebsiteReviewsPage />} />
    </Routes>
    </>
  );
}

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Initialize Capacitor native features
  useEffect(() => {
    if (isNative) {
      document.documentElement.classList.add('capacitor');
      splashScreen.hide();
      statusBar.setDark();
      appLifecycle.onDeepLink((url) => {
        const hashIdx = url.indexOf('#');
        if (hashIdx !== -1) window.location.hash = url.substring(hashIdx);
      });
    }
  }, []);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <OfflineBanner isOffline={isOffline} />
        <div className="pb-[env(safe-area-inset-bottom)] md:pb-0">
          <AppRoutes />
          <Footer />
        </div>
        <MobileBottomNav />
        <PWAInstallPrompt />
        <IOSInstallGuide />
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
